import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useHistory } from 'react-router-dom';
import MaterialTable, { MTableToolbar } from 'material-table';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL, BOM_STAGES, MANUFACTURING_ENGINEER, SUPPLIER, PROCUREMENT_ENGINEER, DESIGN_ENGINEER, SUPER_ADMIN, DELIVERY_STATUS, SALES, LOGISTIC} from 'assets/constants/Constants';
import { STAGE_COLORS } from 'assets/constants/Colors';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
    EDIT_ICON,
    TRASH_ICON,
    CALENDER_ICON,
    EYE_ICON,
    DUPLICATE_ICON,
    FormatListNumberedIcon,
    CheckCircleSharpIcon,
    CancelIcon
} from 'assets/constants/Icons';
import ShowToast from 'components/ShowToast';
import moment from 'moment';
import Spinner from 'components/Spinner';
import { hasPermission } from 'components/Permission';
import { getBomList, deleteBom, createBOM, getBomById, getUserDetails, getPOListForBom} from 'lib/axiosUtils';
import {
    ThemeProvider,
    createTheme,
    Chip,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip
} from '@mui/material';
import './bom.css';
import { DELIVERY_CHIP_COLORS } from 'assets/constants/Constants';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import BOMViewModal from 'components/BOMViewModal';

const genericColumnList = [
    { title: 'Document No.', field: 'documentNumber',render: (rowData) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          <span>{rowData.documentNumber}</span>
    
          {rowData.partial_procurement && (
            <i
              className="fa fa-exclamation-triangle icon"
              title="Partial Procurement"
              style={{
                fontSize: '14px',
                marginLeft: '6px',
                color: '#c62828',
                cursor: 'pointer'
              }}
            />
          )}
        </span>
      ), },
    { title: 'Fixture Name', field: 'fixtureName' },
    { title: 'Customer Name', field: 'customerName' },
    { title: 'Supplier Name', field: 'supplierName', export: true},
    { title: 'End Client', field: 'endClient' },
    {
        title: 'Business Vertical',
        field: 'businessVerticalName',
    },
    { title: 'Region', field: 'regionName' },
    { title: 'DAP Date', field: 'dapDateRaw',
        render: row =>row.dapDate, 
    },
    {
        title: 'Projected Completion Date',
        field: 'projectedcompletiondateRaw',
        render: rowData => rowData.projectedcompletiondate,
    },
    {
        title: 'Planned Completion Date',
        field: 'manufacturingDeliveryRaw',
        render: rowData => rowData.manufacturingDelivery,
    },
    // {
    //     title: 'Procurement Status',
    //     field: 'procurement_calculated_status',
    //     render: (rowData) => {
    //         if (rowData.partial_procurement) {
    //              return <Chip label="Incomplete" style={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold' }} size="small" />;
    //         }

    //         const items = rowData.items || [];
    //         if (items.length === 0) {
    //             return <Chip label="No Items" style={{ backgroundColor: '#9e9e9e', color: 'white' }} size="small" />;
    //         }

    //         const allProcured = items.every(item => item.procurement_done === true);
    //         const anyProcured = items.some(item => item.procurement_done === true);

    //         if (allProcured) {
    //             return <Chip label="Complete" style={{ backgroundColor: '#4caf50', color: 'white' }} size="small" />;
    //         } else if (anyProcured) {
    //             return <Chip label="Incomplete" style={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold' }} size="small" />;
    //         } else {
    //             return <Chip label="Pending" style={{ backgroundColor: '#f44336', color: 'white' }} size="small" />;
    //         }
    //     },
    //     customSort: (a, b) => {
    //         const getScore = (row) => {
    //             if (row.partial_procurement) return 2;
    //             const items = row.items || [];
    //             if (items.every(i => i.procurement_done)) return 3;
    //             return 1;
    //         };
    //         return getScore(a) - getScore(b);
    //     }
    // },
    {
        title: 'Stage',
        field: 'stage',
        render: (rowData) => {
            const stage = rowData.stage;
            let backgroundColor;
            switch (stage) {
                case BOM_STAGES.DESIGN: backgroundColor = STAGE_COLORS.DESIGN; break;
                case BOM_STAGES.PROCUREMENT: backgroundColor = STAGE_COLORS.PROCUREMENT; break;
                case BOM_STAGES.MANUFACTURING_PLANNING: backgroundColor = STAGE_COLORS.MANUFACTURING_PLANNING; break;
                case BOM_STAGES.MANUFACTURING_EXECUTION: backgroundColor = STAGE_COLORS.MANUFACTURING_EXECUTION; break;
                case BOM_STAGES.TESTING: backgroundColor = STAGE_COLORS.TESTING; break;
                case BOM_STAGES.DISPATCH: backgroundColor = STAGE_COLORS.DISPATCH; break;
                case BOM_STAGES.HANDOVER_TO_DISPATCH: backgroundColor = STAGE_COLORS.HANDOVER_TO_DISPATCH; break;
                case BOM_STAGES.COMMERCIAL_CLEARANCE: backgroundColor = STAGE_COLORS.COMMERCIAL_CLEARANCE; break;
                case BOM_STAGES.COMPLETED: backgroundColor = STAGE_COLORS.COMPLETED; break;
                case BOM_STAGES.VALIDATION: {
                    const isValCompleted = rowData.validation_completed || rowData.validation_details?.validation_completed;
                    backgroundColor = isValCompleted ? STAGE_COLORS.VALIDATION_COMPLETED : STAGE_COLORS.VALIDATION;
                    break;
                }
                default: backgroundColor = '#616161'; break;
            }
            const displayStage = stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : 'N/A';
            return <Chip label={displayStage} style={{ width: '140px', color: 'black', backgroundColor, fontSize: '10px' }} />;
        },
    },

    {
        title: "Searchable",
        field: "searchableCombined",
        hidden: true,
        searchable: true,
        export: false,
    },
];

const supplierColumnList = [
    { title: 'Document No.', field: 'documentNumber' },
    { title: 'Fixture Name', field: 'fixtureName' },
    {
        title: 'Projected Completion Date',
        field: 'projectedcompletiondate',
    },
    {
        title: 'Planned Completion Date',
        field: 'manufacturingDelivery',
    },
    {
        title: "Searchable",
        field: "searchableCombined",
        hidden: true,
        searchable: true,
        export: false,
    },
    {
        title: 'Status',
        field: 'current_tracking_status',
        render: (rowData) => {
            const key = rowData.current_tracking_status;
            const colors = DELIVERY_CHIP_COLORS[key] || { background: '#9e9e9e', text: 'white' };
            const label = getStatusLabel(rowData.current_tracking_status);
            return label === '-' ? null :
                (
                    <Chip
                        label={label}
                        size="small"
                        style={{ backgroundColor: colors.background, color: colors.text, fontWeight: 400 }}
                    />
                );
        },
    },
];
const paymentColumn = {
        title: 'Payment',
        field: 'paymentStatus',
        align: 'center',
        filtering: true,
        sorting: true,
        render: (rowData) => {
            const status = String(rowData.paymentStatus || '').trim();
            const normalizedStatus = status.toLowerCase();

            if (normalizedStatus === 'received') {
            return (
                <Tooltip title="Received" arrow>
                    <span>
                        <CheckCircleSharpIcon
                            style={{
                                color: '#008000',
                                fontSize: '30px',
                                width: '30px',
                                height: '30px'
                            }}
                        />
                    </span>
                </Tooltip>
            );
        }

        if (normalizedStatus === 'not received') {
            return (
                <Tooltip title="Not Received" arrow>
                    <span>
                        <CancelIcon
                            style={{
                                color: '#f44336',
                                fontSize: '30px',
                                width: '30px',
                                height: '30px'
                            }}
                        />
                    </span>
                </Tooltip>
            );
        }

        return '-';
    },
    export: true,
};

const getStatusLabel = (status) => {
        if (!status) return 'N/A';
        return status;
    };

const handleCustomExport = (columns, data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('BOM List');

    const exportableColumns = columns
        .filter(col => col.field && (col.export !== false))
        .map(col => ({
            header: col.title,
            key: col.field,
            width: 10
        }));

    worksheet.columns = exportableColumns;

    const headerRow = worksheet.getRow(1);

    const maxWidths = {};
    exportableColumns.forEach(col => {
        maxWidths[col.key] = String(col.header).length;
    });

    data.forEach(rowData => {
        exportableColumns.forEach(col => {
            const cellValue = rowData[col.key] ? String(rowData[col.key]) : '-';
            maxWidths[col.key] = Math.max(maxWidths[col.key], cellValue.length);
        });
    });

    exportableColumns.forEach(col => {
        col.width = maxWidths[col.key] + 2;
    });
    worksheet.columns = exportableColumns;

    headerRow.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDCE6F1' }
        };
        cell.font = {
            bold: true,
            color: { argb: 'FF000000' },
            size: 12
        };
        cell.alignment = {
            vertical: 'middle',
            horizontal: 'left'
        };
        cell.border = {
            bottom: { style: 'thin' }
        };
    });

    data.forEach(rowData => {
        const row = {};
        exportableColumns.forEach(col => {
            row[col.key] = rowData[col.key] || '-';
        });
        worksheet.addRow(row);
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `BOM_List_${moment().format('YYYY-MM-DD')}.xlsx`);
    });
};

const handleExportPdf = (columns, data) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const logoUrl = '/images/logo512.png';

    const exportableColumns = columns
        .filter(col => col.field && (col.export !== false))
        .map(col => ({
            header: col.title,
            dataKey: col.field
        }));

    const tableData = data.map(rowData => {
        const row = {};
        exportableColumns.forEach(col => {
            row[col.dataKey] = rowData[col.dataKey] ? String(rowData[col.dataKey]) : '-';
        });
        return row;
    });

    doc.autoTable({
        startY: 30, 
        margin: { top: 30 }, 
        columns: exportableColumns,
        body: tableData,
        styles: { fontSize: 8 },
        headStyles: {
            fillColor: [220, 230, 241],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0,
            lineColor: [0, 0, 0] 
        },
        theme: 'grid',
        didDrawPage: (data) => {
            try {
                const imgProps = doc.getImageProperties(logoUrl);
                
                const pdfLogoHeight = 15; 
                const pdfLogoWidth = (imgProps.width * pdfLogoHeight) / imgProps.height;

                doc.addImage(logoUrl, 'JPEG', data.settings.margin.left, 10, pdfLogoWidth, pdfLogoHeight);
            } catch (err) {
                console.warn("Logo could not be loaded", err);
            }

            const str = 'Downloaded on: ' + moment().format('DD-MMM-YYYY HH:mm:ss');
            doc.setFontSize(8);
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            doc.text(str, data.settings.margin.left, pageHeight - 10);
        }
    });

    doc.save(`BOM_List_${moment().format('YYYY-MM-DD')}.pdf`);
};

const BomListPage = () => {
    const history = useHistory();
    const [bomList, setBomList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [tableActionsArray, setTableActionsArray] = useState([]);
    const mytheme = createTheme({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [masterBomList, setMasterBomList] = useState([]); // <-- Add this
    const [selectedStage, setSelectedStage] = useState('All');
    const [poModalOpen, setPoModalOpen] = useState(false);
    const [poListForBom, setPoListForBom] = useState([]);
    const [selectedPoId, setSelectedPoId] = useState('');
    const [selectedPoNumber, setSelectedPoNumber] = useState('');
    const [poLoading, setPoLoading] = useState(false);


    const handleOpen = (rowData) => {
        setSelectedRowData(rowData);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedRowData(null);
    };
    const initialUser =
       JSON.parse(localStorage.getItem('userData') || 'null') || {};

    useEffect(() => {
        fetchBomData();
    }, []);

    const handleStageChange = (event, newStage) => {
        if (newStage !== null) {
            setSelectedStage(newStage);
        }
    };

    useEffect(() => {
        if (selectedStage === 'All') {
            setBomList(masterBomList);
        } else {
            const filtered = masterBomList.filter(
                (bom) => bom.stage === selectedStage
            );
            setBomList(filtered);
        }
    }, [selectedStage, masterBomList]);

    const fetchSupplierNames = async (supplierIds) => {
        const map = {};
        await Promise.all(
          supplierIds.map(async (id) => {
            const res = await getUserDetails({}, {}, id);
            const user = res?.data?.result;
            map[id] =
              `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '-';
          })
        );
        return map;
      };

    const fetchBomData = async () => {
        setIsLoading(true);
    try {
            
        const response = await getBomList();
        if (response?.data?.result) {
            let data = response.data.result;
            if(initialUser.user_role === SUPPLIER){
                data = data.filter(bom => bom.supplier_id === initialUser._id && bom.current_stage === BOM_STAGES.MANUFACTURING_EXECUTION);
            } else if(initialUser.user_role === PROCUREMENT_ENGINEER){
                const allowedStages = [
                    BOM_STAGES.PROCUREMENT,
                    BOM_STAGES.MANUFACTURING_PLANNING,
                    BOM_STAGES.MANUFACTURING_EXECUTION,
                    BOM_STAGES.TESTING,
                    BOM_STAGES.DISPATCH,
                    BOM_STAGES.COMMERCIAL_CLEARANCE,
                    BOM_STAGES.HANDOVER_TO_DISPATCH,
                    BOM_STAGES.COMPLETED
                ];
                data = data.filter(bom => allowedStages.includes(bom.current_stage));
            } else if (initialUser.user_role === MANUFACTURING_ENGINEER) {
                const allowedStages = [
                   BOM_STAGES.MANUFACTURING_PLANNING,
                   BOM_STAGES.MANUFACTURING_EXECUTION,
                   BOM_STAGES.TESTING,
                   BOM_STAGES.DISPATCH,
                   BOM_STAGES.COMMERCIAL_CLEARANCE,
                   BOM_STAGES.HANDOVER_TO_DISPATCH,
                   BOM_STAGES.COMPLETED
               ];
                data = data.filter(bom => allowedStages.includes(bom.current_stage));
            } else if (initialUser.user_role === SALES) {
                const assignedRegionIds = Array.isArray(initialUser.region_id)
                ? initialUser.region_id
                : [];

                data = data.filter(bom =>
                  assignedRegionIds.includes(
                    typeof bom.region_id === 'object'
                      ? bom.region_id._id
                      : bom.region_id
                  )
                );
            } else if (initialUser.user_role === LOGISTIC) {
                const allowedStages = [
                    BOM_STAGES.COMMERCIAL_CLEARANCE,
                    BOM_STAGES.HANDOVER_TO_DISPATCH
                ];
                data = data.filter(bom => allowedStages.includes(bom.current_stage));
            }
            const supplierIds = [
                ...new Set(
                    data
                        .map(bom => bom.supplier_id)
                        .filter(Boolean)
                )
            ];
            const supplierMap = await fetchSupplierNames(supplierIds);
            const mappedData = data.map((bom, index) => ({
                ...bom,
                index: index + 1,
                bomId: bom._id,
                documentNumber: bom.fixture_document_number,
                fixtureName: bom.fixture_name,
                supplierName: supplierMap[bom.supplier_id] || '-',
                manufacturing_release_date: bom.manufacturing_release_date ? moment(bom.manufacturing_release_date).format('DD-MM-YYYY') : '-',
                stage: bom.current_stage,
                dapDate: bom.dap_date ? moment(bom.dap_date).format('DD-MM-YYYY') : '',
                dapDateRaw: bom.dap_date ? new Date(bom.dap_date).getTime() : Number.MAX_SAFE_INTEGER,
                procurementDelivery: bom.procurement_expected_delivery_date
                    ? moment(bom.procurement_expected_delivery_date).format('DD-MM-YYYY')
                    : '',
                procurementDeliveryRaw: bom.procurement_expected_delivery_date
                    ? new Date(bom.procurement_expected_delivery_date).getTime()
                    : Number.MAX_SAFE_INTEGER,
                manufacturingDelivery: bom.mfg_delivery_date
                    ? moment(bom.mfg_delivery_date).format('DD-MM-YYYY')
                    : '',
                manufacturingDeliveryRaw: bom.mfg_delivery_date
                    ? new Date(bom.mfg_delivery_date).getTime()
                    : Number.MAX_SAFE_INTEGER,
                projectedcompletiondate: bom.projected_completion_date
                    ? moment(bom.projected_completion_date)
                        .add(Number(bom.testing_buffer_days) || 0, 'days')
                        .format('DD-MM-YYYY')
                    : '',
                projectedcompletiondateRaw: bom.projected_completion_date
                    ? moment(bom.projected_completion_date)
                        .add(Number(bom.testing_buffer_days) || 0, 'days')
                        .toDate()
                        .getTime()
                    : Number.MAX_SAFE_INTEGER,

                endClient: bom.end_client,
                paymentStatus: bom.payment_status || bom.commercial_details?.payment_status || '-',
            }));
                setMasterBomList(mappedData);
                setBomList([...mappedData]);
        }
    } catch (error) {
        console.error('Error fetching BOMs:', error);
        ShowToast('Failed to fetch BOM list. Please try again.', TYPE_FAIL, INTERVAL);
    } finally {
        setIsLoading(false);
    }
};

    /*const createProgram = () => {
        history.push('/create-bom-page');
    };*/
    const createProgram = async () => {
    setPoModalOpen(true);
    setPoLoading(true);

    try {
        const response = await getPOListForBom({}, {});
        setPoListForBom(response?.data?.result || []);
    } catch (error) {
        ShowToast('Failed to load PO list', TYPE_FAIL);
    } finally {
        setPoLoading(false);
    }
};
    const handleContinueWithPo = () => {
    if (!selectedPoId) {
        ShowToast('Please select a PO', TYPE_FAIL);
        return;
    }

    setPoModalOpen(false);
    history.push(`/create-bom-page?poId=${selectedPoId}`);
};

    const handleSkipPo = () => {
        setPoModalOpen(false);
        history.push('/create-bom-page');
    };


    const editProgram = (row) => {
        const id = row?.bomId || row?._id;
        if (!id) return console.warn('No BOM id on row:', row);
        history.push(`/create-bom-page/${id}`);
    };

    const handleDelete = async (bomId) => {
        if (window.confirm('Are you sure you want to delete this BOM?')) {
            setIsLoading(true);
            try {
                await deleteBom({}, {}, bomId);
                ShowToast('BOM deleted successfully', TYPE_SUCCESS, INTERVAL);
                await fetchBomData();
            } catch (error) {
                setIsLoading(false);
                ShowToast('Something went wrong. Please try again.', TYPE_FAIL, INTERVAL);
            }
        }
    };
    
    const handleDuplicate = async (rowData) => {
        if (!rowData)return;

        if (window.confirm('Are you sure you want to Duplicate this BOM?')) {
      
        try {
          const response = await getBomById({}, {}, rowData.bomId);
           if (!response?.data?.result) {
                ShowToast('Failed to fetch BOM details', TYPE_FAIL, INTERVAL);
                return;
            }
          const bomData = response.data.result;
          //Remove unwanted fields and create a duplicate
          const duplicatedBom = {
            ...bomData,
            _id: undefined,
            bomId: undefined,
            fixture_document_number: `COPY - ${bomData.fixture_document_number || ''}`,
            fixture_name: `Copy - ${bomData.fixture_name || ''}`,
            stage: BOM_STAGES.DESIGN,
          };
          //Merge items having same part_ref & document_number
            const mergedItemsMap = new Map();

          (duplicatedBom.items || []).forEach((item) => {
            const key = `${item.part_ref}-${item.document_number}`; // unique identifier

            if (mergedItemsMap.has(key)) {
                const existing = mergedItemsMap.get(key);
                // Add quantities if same part_ref + doc number
                existing.quantity = Number(existing.quantity || 0) + Number(item.quantity || 0);
                mergedItemsMap.set(key, existing);
            } else {
                // Add a new unique entry
                mergedItemsMap.set(key, {
                ...item,
                quantity: Number(item.quantity || 0),
                });
            }
          });
          const mergedItems = Array.from(mergedItemsMap.values());
          const payload = {
            design_engineer_id: duplicatedBom.design_engineer_id,
            fixture_document_number: duplicatedBom.fixture_document_number,
            fixture_name: duplicatedBom.fixture_name,
            manufacturing_release_date: duplicatedBom.manufacturing_release_date
                ? new Date(duplicatedBom.manufacturing_release_date)
                : undefined,
            customer_id: duplicatedBom.customer_id,
            end_client: duplicatedBom.end_client || undefined,
            machine: duplicatedBom.machine,
            component_drawing_numbers: duplicatedBom.component_drawing_numbers || [],
            po_reference: duplicatedBom.po_reference,
            dap_date: duplicatedBom.dap_date ? new Date(duplicatedBom.dap_date) : undefined,
            surface_treatment_type: duplicatedBom.surface_treatment_type,
            business_vertical_id: duplicatedBom.business_vertical_id,
            region_id: duplicatedBom.region_id,
            items: mergedItems.map((it) => ({
                part_ref: it.part_ref,
                process_type_id: it.process_type_id,
                steel_material_description: it.steel_material_description,
                finished_material_od_mm: Number(it.finished_material_od_mm),
                finished_material_length_mm: Number(it.finished_material_length_mm),
                quantity: Number(it.quantity),
                document_number: it.document_number,
                procurement_done: '',
                attachments: Array.isArray(it.attachments) ? it.attachments : [],
            })),
          };
          //Create a new BOM (duplicate)
          const res = await createBOM({}, payload);
          const newBomData = res.data.result;
          const mappedNewBom = 
          {
            ...newBomData,
            bomId: newBomData._id,
            documentNumber: newBomData.fixture_document_number,
            fixtureName: newBomData.fixture_name,
            stage: newBomData.current_stage || BOM_STAGES.DESIGN,
            dapDate: newBomData.dap_date ? moment(newBomData.dap_date).format('DD-MM-YYYY') : '',
            procurementDelivery: newBomData.procurement_expected_delivery_date ? moment(newBomData.procurement_expected_delivery_date).format('DD-MM-YYYY') : '',
            manufacturingDelivery: newBomData.mfg_delivery_date ? moment(newBomData.mfg_delivery_date).format('DD-MM-YYYY') : '',
            endClient:newBomData.end_client,
          };
          //Update UI immediately
          setBomList((prev) => [...prev, mappedNewBom]);
          ShowToast('BOM duplicated successfully', TYPE_SUCCESS, INTERVAL);
          await fetchBomData();
        } catch (err) {
          console.error("Error duplicating BOM:", err);
          ShowToast('Failed to duplicate BOM', TYPE_FAIL, INTERVAL);
        }
    }
    };
        
    useEffect(() => {
        const actions = [];
        if (hasPermission('bom', 'read') && initialUser.user_role !== SUPPLIER ) {
            actions.push({
                icon: () => <i className={`${EYE_ICON}`} title="View BOM"></i>,
                onClick: (event, rowData) => handleOpen(rowData),
            });
        }
        if (hasPermission('bom', 'update')) {
            actions.push((rowData) =>{
                const isSalesUser = initialUser.user_role === SALES;
                const stageValue = rowData.stage || rowData.current_stage || "";
                console.log("stage value:", stageValue);
                const isValidationStage = 
                    stageValue.toLowerCase().includes("validation");
                const canEdit = isSalesUser ? isValidationStage : true;
                return {
                    icon: () => ( <i
                        className={`${EDIT_ICON}`}
                        title="Edit BOM"
                        style={{
                            color: canEdit ? undefined : '#bdbdbd',
                            cursor: canEdit ? 'pointer': 'not-allowed',
                            opacity: canEdit ? 1 : 0.6,
                        }}
                    ></i>
                    ),
                    tooltip: "Edit BOM",
                    disabled: !canEdit,
                    onClick: (event, row) =>{
                        if(!canEdit) return;
                        editProgram(row);       
                    },
                };
            });
        }
        if (hasPermission('bom', 'create') &&
        (initialUser.user_role === DESIGN_ENGINEER || initialUser.user_role === SUPER_ADMIN)) {
            actions.push((rowData) => {
                const isCompleted = rowData.stage != BOM_STAGES.DESIGN;
                return {
                    icon: () => (
                        <i
                            className={`${DUPLICATE_ICON}`}
                            title="Duplicate BOM"
                            style={{
                                color: isCompleted ? undefined : '#bdbdbd',
                                cursor: isCompleted ? 'pointer' : 'not-allowed',
                                opacity: isCompleted ? 1 : 0.6,
                            }}
                        ></i>
                    ),
                    tooltip: "Duplicate BOM",
                    disabled: !isCompleted,
                    onClick: (event, row) => {
                        if (!isCompleted) return;
                        handleDuplicate(row);
                    },
                };
            });
        }
        if (hasPermission('bom', 'delete')) {
            actions.push({
                icon: () => <i className={`${TRASH_ICON}`} title="Delete BOM"></i>,
                onClick: (event, rowData) => handleDelete(rowData.bomId),
            });
        }
        setTableActionsArray(actions);
    }, []);




    const tableOptions = useMemo(() => ({
        actionsColumnIndex: -1,
        headerStyle: {
            fontWeight: 'bolder',
            fontSize: 14,
            textAlign: 'center'
        },
        searchFieldStyle: {
            width: '120px',
            maxWidth: '120px',
        },
        exportAllData: true,
        filtering: true,
        sorting: true,
        columnsButton: true,
        exportButton: true,
        exportFileName: 'BOM List',
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        rowStyle: (rowData) => {
            if (rowData.partial_procurement) {
                return { backgroundColor: '#fff3e0' }; // Light Orange for visibility
            }
            return {};
        },
        cellStyle:{
            fontSize: 12,
        },
        exportCsv: (columns, data) => {
            handleCustomExport(columns, data);
        },
        exportPdf: (columns, data) => {
            handleExportPdf(columns, data);
        }
    }), []);

    const getVisibleFilterStages = (role) => {
        const allStages = [
            'All',
            BOM_STAGES.DESIGN,
            BOM_STAGES.PROCUREMENT,
            BOM_STAGES.MANUFACTURING_PLANNING,
            BOM_STAGES.MANUFACTURING_EXECUTION,
            BOM_STAGES.TESTING,
            BOM_STAGES.DISPATCH,
            BOM_STAGES.COMMERCIAL_CLEARANCE,
            BOM_STAGES.HANDOVER_TO_DISPATCH,
            BOM_STAGES.COMPLETED,
            BOM_STAGES.VALIDATION,
        ];

        if (role === PROCUREMENT_ENGINEER) {
            return [
                'All',
                BOM_STAGES.PROCUREMENT,
                BOM_STAGES.MANUFACTURING_PLANNING,
                BOM_STAGES.MANUFACTURING_EXECUTION,
                BOM_STAGES.TESTING,
                BOM_STAGES.DISPATCH,
                BOM_STAGES.COMMERCIAL_CLEARANCE,
                BOM_STAGES.HANDOVER_TO_DISPATCH,
                BOM_STAGES.COMPLETED,
            ];
        }

        if (role === MANUFACTURING_ENGINEER) {
            return [
                'All',
                BOM_STAGES.MANUFACTURING_PLANNING,
                BOM_STAGES.MANUFACTURING_EXECUTION,
                BOM_STAGES.TESTING,
                BOM_STAGES.DISPATCH,
                BOM_STAGES.COMMERCIAL_CLEARANCE,
                BOM_STAGES.HANDOVER_TO_DISPATCH,
                BOM_STAGES.COMPLETED,
            ];
        }

        if (role === LOGISTIC) {
            return [
                'All',
                BOM_STAGES.COMMERCIAL_CLEARANCE,
                BOM_STAGES.HANDOVER_TO_DISPATCH,
            ];
        }

        return allStages;
    };

    const filterStages = getVisibleFilterStages(initialUser.user_role);

    const getStageColor = (stage) => {
        switch (stage) {
            case BOM_STAGES.DESIGN:                 
                return STAGE_COLORS.DESIGN;
            case BOM_STAGES.PROCUREMENT:            
                return STAGE_COLORS.PROCUREMENT;
            case BOM_STAGES.MANUFACTURING_PLANNING: 
                return STAGE_COLORS.MANUFACTURING_PLANNING;
            case BOM_STAGES.MANUFACTURING_EXECUTION:
                return STAGE_COLORS.MANUFACTURING_EXECUTION;
            case BOM_STAGES.TESTING:                
                return STAGE_COLORS.TESTING;
            case BOM_STAGES.DISPATCH:               
                return STAGE_COLORS.DISPATCH;
            case BOM_STAGES.COMPLETED:              
                return STAGE_COLORS.COMPLETED;
            case BOM_STAGES.COMMERCIAL_CLEARANCE:   
                return STAGE_COLORS.COMMERCIAL_CLEARANCE;
            case BOM_STAGES.HANDOVER_TO_DISPATCH:   
                return STAGE_COLORS.HANDOVER_TO_DISPATCH;
            case 'All':                             
                return '#9e9e9e';
            case BOM_STAGES.VALIDATION:   
            return STAGE_COLORS.VALIDATION;
            default:                                
                return '#9e9e9e';
        }
    };

const CustomToolbar = (props) => (
        <div>
            <MTableToolbar {...props} />
            <Box sx={{ padding: { xs: '15px 8px 12px', sm: '0 16px 16px', md: '0 24px 16px' },
                mt: 0,
                overflowX: 'auto',
                width: '100%',
                display: 'flex',
                }}
            >
                <ToggleButtonGroup
                    value={selectedStage}
                    exclusive
                    onChange={handleStageChange}
                    size="small"
                    aria-label="Filter by stage"
                    sx={{ display: 'flex', flexWrap: 'nowrap' }}
                >
                    {filterStages.map((stage) => {
                        const stageColor = getStageColor(stage);
                        
                        return (
                            <ToggleButton
                                key={stage}
                                value={stage}
                                sx={{
                                    textTransform: 'capitalize',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    flexShrink: 0,
                                    
                                    color: 'black',
                                    backgroundColor: stageColor,
                                    border: '1px solid rgba(0,0,0,0.1)',

                                    '&.Mui-selected': {
                                        backgroundColor: stageColor,
                                        color: 'black',
                                        fontWeight: 700,
                                        boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.2)',
                                        opacity: 2,
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: stageColor,
                                        opacity: 0.9,
                                    },
                                    '&:not(.Mui-selected):hover': {
                                        backgroundColor: stageColor,
                                        opacity: 0.75,
                                    },
                                    '&:not(.Mui-selected)': {
                                        opacity: 1, 
                                    }
                                }}
                            >
                                {stage}
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>
            </Box>
        </div>
    );
    const tableComponents = useMemo(() => ({
        Toolbar: (props) => initialUser.user_role === SUPPLIER ? <MTableToolbar {...props} /> : <CustomToolbar {...props} />,
    }), [initialUser.user_role, selectedStage, filterStages]);
    const poOptions = poListForBom.map((po) => ({
    value: po._id,
    label:
        po?.po_number ||
        po?.poNumber ||
        po?.po_reference ||
        po?.poReference ||
        po?._id,
}));
    const tableColumns = useMemo(() => {
    if (initialUser.user_role === SUPPLIER) {
        return supplierColumnList;
    }

    if (initialUser.user_role === SALES) {
        const columns = [...genericColumnList];
        const stageIndex = columns.findIndex((col) => col.field === 'stage');

        if (stageIndex >= 0) {
            columns.splice(stageIndex, 0, paymentColumn);
        } else {
            columns.push(paymentColumn);
        }

        return columns;
    }

        return genericColumnList;
    }, [initialUser.user_role]);

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <div className="main-content bom-page">
                    <div className="pagetitle row" style={{ color: 'black', paddingTop: 15, marginTop: "-25px" }}>
                        <div className="col-md-10 sm_device" style={{ display: 'flex', alignItems: 'center' }}>
                            <FormatListNumberedIcon aria-hidden="true" />
                            <span style={{ marginLeft: '7px', marginRight: '10px' }}>BOM</span>
                            
                            {hasPermission('bom', 'create') && (
                            <AddCircleIcon  
                                onClick={createProgram}
                                style={{ 
                                    cursor: 'pointer', 
                                    color: '#1976d2', 
                                    fontSize: '30px !important',
                                }}
                                titleAccess="Create BOM"
                                
                            />
                        )}
                    </div>
                    <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                        <i className={`${CALENDER_ICON}`}></i> {moment().format('DD MMM YYYY')}
                    </div>
                </div>

                    <div
                        style={{
                            borderBottom: '1px solid lightgray',
                            marginBottom: 15,
                            marginTop: 10,
                        }}
                    />
                    <div className="list-wrapper">
                        {/* {hasPermission('bom', 'create') && (
                            <div className="form-button-div">
                                <button className="brand-button" onClick={createProgram}>
                                    Create BOM
                                </button>
                            </div>
                        )} */}
                        <div
                            className="mt-4 border list-table-div project-list-table-div bom-table-div"
                            style={{ marginBottom: 50 }}
                        >
                            <ThemeProvider theme={mytheme}>
                                <MaterialTable
                                    columns={tableColumns}
                                    data={bomList}
                                    actions={tableActionsArray}
                                    options={tableOptions}
                                    components={tableComponents}
                                />
                            </ThemeProvider>
                            {isModalOpen && selectedRowData && (
                                BOMViewModal(handleClose, selectedRowData)
                            )}
                        </div>
                    </div>
                </div>
            )}
            <Dialog 
                open={poModalOpen} 
                onClose={() => setPoModalOpen(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '8px' }
                }}
            >
                <DialogTitle className="bom-dialog-title-bar" style={{ padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span className="bom-dialog-title-text" style={{ color: '#2d3d42' }}>
                            Select Purchase Order
                        </span>
                        <span className="bom-dialog-subtitle">
                            Please select a PO to continue creating the BOM
                        </span>
                    </div>
                </DialogTitle>

                <DialogContent style={{ padding: '24px' }}>
                    {poLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Spinner />
                        </div>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <div className="dialog-input-label" style={{ marginBottom: '8px', color: '#333' }}>
                                Purchase Order Number <span style={{ color: 'red' }}>*</span>
                            </div>
                            <Select
                                placeholder="Search and select PO..."
                                options={poOptions}
                                value={poOptions.find((opt) => opt.value === selectedPoId) || null}
                                onChange={(option) => {
                                    setSelectedPoId(option?.value || '');
                                    setSelectedPoNumber(option?.label || '');
                                }}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuPlacement="auto"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: '42px',
                                        fontSize: '14px',
                                        borderRadius: '6px',
                                        borderColor: state.isFocused ? '#2d3d42' : '#e5e7eb',
                                        boxShadow: state.isFocused ? '0 0 0 1px #2d3d42' : 'none',
                                        '&:hover': {
                                            borderColor: '#2d3d42'
                                        }
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '14px',
                                        backgroundColor: state.isSelected ? '#2d3d42' : state.isFocused ? '#f0f0f0' : 'transparent',
                                        color: state.isSelected ? 'white' : '#333',
                                        cursor: 'pointer'
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        fontSize: '14px',
                                        color: '#333'
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        fontSize: '14px',
                                        color: '#999'
                                    }),
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions className="bom-dialog-actions" style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#f9f9f9', display: 'flex', justifyContent: 'space-between' }}>
                    <Button 
                        className="bom-dialog-btn"
                        onClick={() => setPoModalOpen(false)}
                        sx={{ color: '#666', border: '1px solid #ddd' }}
                    >
                        Cancel
                    </Button>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button 
                            className="bom-dialog-btn"
                            onClick={handleSkipPo}
                            sx={{ color: '#2d3d42', border: '1px solid #2d3d42' }}
                        >
                            Skip without PO
                        </Button>
                        <Button 
                            variant="contained" 
                            className="bom-dialog-btn"
                            onClick={handleContinueWithPo}
                            disabled={!selectedPoId}
                            sx={{ 
                                backgroundColor: '#2d3d42', 
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#1a2629'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#cccccc',
                                    color: '#666666'
                                }
                            }}
                        >
                            Continue to Create BOM
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BomListPage;

